import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Coffee } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { money, PAYMENT_METHODS, TREATMENTS } from "../lib/format";

const empty = () => ({
  date: new Date().toISOString().slice(0, 10),
  treatment: "standard",
  food_split: false,
  gst_free_percent: "",
  eftpos: "",
  cash: "",
  card: "",
  other: "",
});

export default function DailyTakingsDialog({ open, onOpenChange, onDone, projectId }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const round5c = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return v;
    return (Math.round(n * 20) / 20).toFixed(2);
  };

  const lines = [
    { key: "eftpos", method: "eftpos" },
    { key: "cash", method: "cash" },
    { key: "card", method: "card" },
    { key: "other", method: "other" },
  ];
  const total = lines.reduce((s, l) => s + (parseFloat(form[l.key]) || 0), 0);

  const submit = async () => {
    const entries = lines
      .map((l) => ({ method: l.method, amount: parseFloat(form[l.key]) || 0 }))
      .filter((l) => l.amount > 0);
    if (entries.length === 0) { toast.error("Enter at least one amount"); return; }
    setSaving(true);
    const pct = form.food_split ? Math.min(Math.max(parseFloat(form.gst_free_percent) || 0, 0), 100) : 0;
    const posts = [];
    entries.forEach((e) => {
      if (pct > 0) {
        const freeAmt = Math.round(e.amount * (pct / 100) * 100) / 100;
        const taxable = Math.round((e.amount - freeAmt) * 100) / 100;
        if (freeAmt > 0) posts.push({ method: e.method, amount: freeAmt, treatment: "gst_free", tag: "GST-free" });
        if (taxable > 0) posts.push({ method: e.method, amount: taxable, treatment: "standard", tag: "Taxable" });
      } else {
        posts.push({ method: e.method, amount: e.amount, treatment: form.treatment, tag: "" });
      }
    });
    try {
      await Promise.all(posts.map((p) =>
        api.post("/transactions", {
          type: "income",
          date: form.date,
          description: `Daily takings — ${PAYMENT_METHODS[p.method]}${p.tag ? ` (${p.tag})` : ""}`,
          party: "",
          category: "Daily Takings",
          treatment: p.treatment,
          amount: p.amount,
          payment_method: p.method,
          project_id: projectId || "",
        })
      ));
      const cardEntry = entries.find((e) => e.method === "card");
      if (cardEntry) {
        try { await api.post("/loans/apply-auto-repay", { card_amount: cardEntry.amount, date: form.date }); } catch { /* ignore */ }
      }
      toast.success("Daily takings recorded");
      onOpenChange(false);
      setForm(empty());
      onDone && onDone();
    } catch {
      toast.error("Could not save takings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm(empty()); }}>
      <DialogContent className="sm:max-w-md" data-testid="daily-takings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Coffee className="w-5 h-5 text-primary" /> Daily takings
          </DialogTitle>
          <DialogDescription>
            Log a day's total takings split by payment method — ideal for cafes & retail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" data-testid="takings-date" value={form.date}
                onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>GST treatment</Label>
              <Select value={form.treatment} onValueChange={(v) => set("treatment", v)}>
                <SelectTrigger className="mt-1.5" data-testid="takings-treatment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TREATMENTS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {lines.map((l) => (
              <div key={l.key}>
                <Label>{PAYMENT_METHODS[l.method]} (incl. GST)</Label>
                <Input type="number" step="0.01" data-testid={`takings-${l.key}`} value={form[l.key]}
                  onChange={(e) => set(l.key, e.target.value)}
                  onBlur={l.key === "cash" ? (e) => e.target.value && set("cash", round5c(e.target.value)) : undefined}
                  className="mt-1.5" placeholder="0.00" />
                {l.key === "cash" && <p className="text-[11px] text-muted-foreground mt-1">Auto-rounds to nearest 5¢</p>}
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-border px-3 py-2.5 space-y-3" data-testid="takings-food-split-block">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Split food GST</div>
                <div className="text-xs text-muted-foreground">Part taxable (hot / dine-in), part GST-free (basic food)</div>
              </div>
              <Switch checked={form.food_split} onCheckedChange={(v) => set("food_split", v)} data-testid="takings-food-split" />
            </div>
            {form.food_split && (
              <div>
                <Label className="text-xs text-muted-foreground">GST-free portion %</Label>
                <Input type="number" min="0" max="100" step="1" value={form.gst_free_percent}
                  onChange={(e) => set("gst_free_percent", e.target.value)} className="mt-1.5" data-testid="takings-gstfree-percent" placeholder="e.g. 40" />
                <p className="text-[11px] text-muted-foreground mt-1">The rest is recorded as taxable (10% GST) for an exact BAS.</p>
              </div>
            )}
          </div>
          <div className="rounded-lg bg-secondary px-4 py-3 flex justify-between text-sm">
            <span className="text-muted-foreground">Total takings</span>
            <span className="num font-semibold" data-testid="takings-total">{money(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="takings-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="takings-save">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Record takings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
